import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Search, Plus, Edit, Trash2, Package, AlertTriangle,
  ChevronLeft, ChevronRight, Loader2, X, Calendar, RefreshCw
} from 'lucide-react';
import { fmtCurrency } from '../lib/localeUtils';
import ProductForm, { emptyProduct, PRODUCT_CATEGORIES as CATEGORIES } from '../components/ProductForm';
import { keyed, skeletonKeys } from '../lib/listKeys';
import { addOfflineProduct, generateOfflineId, getPendingProducts } from '../lib/offlineDB';
import { addSyncListener } from '../lib/syncService';
import OfflineFailuresDialog from '../components/OfflineFailuresDialog';

export default function InventoryPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [stockFilter, setStockFilter] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState({ ...emptyProduct });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [showSyncReview, setShowSyncReview] = useState(false);

  const refreshPendingSync = useCallback(() => {
    getPendingProducts().then((rows) => setPendingSyncCount(rows.length)).catch(() => {});
  }, []);
  useEffect(() => {
    refreshPendingSync();
    return addSyncListener((event) => { if (event === 'products_queue_synced') refreshPendingSync(); });
  }, [refreshPendingSync]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/products', {
        params: {
          search,
          category: category === 'All' ? '' : category,
          manufacturer: manufacturerFilter === 'All' ? '' : manufacturerFilter,
          page,
          limit: 20,
          stock_status: stockFilter,
        }
      });
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [search, category, manufacturerFilter, page, stockFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchProducts, 300);
    return () => clearTimeout(timer);
  }, [fetchProducts]);

  useEffect(() => {
    api.get('/products-manufacturers').then(res => {
      setManufacturers(res.data.manufacturers || []);
    }).catch(() => {});
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    const pick = (k) => searchParams.get(k) || '';
    setEditProduct(null);
    setFormData({ ...emptyProduct, name: pick('name'), generic_name: pick('generic_name'), manufacturer: pick('manufacturer'),
      ...(CATEGORIES.includes(pick('category')) ? { category: pick('category') } : {}) });
    setDialogOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const openAdd = () => {
    setEditProduct(null);
    setFormData({ ...emptyProduct });
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setFormData({
      name: product.name || '',
      generic_name: product.generic_name || '',
      category: product.category || 'General',
      manufacturer: product.manufacturer || '',
      unit_price: product.unit_price || 0,
      stock_quantity: product.stock_quantity || 0,
      min_stock_level: product.min_stock_level || 10,
      requires_prescription: product.requires_prescription || false,
      is_otc: product.is_otc === true, is_active: product.is_active !== false, image_url: product.image_url || '',
      photos: product.photos?.length ? product.photos : (product.image_url ? [product.image_url] : []),
      is_controlled: product.is_controlled || false,
      description: product.description || '',
      barcode: product.barcode || '',
      batches: product.batches || []
    });
    setDialogOpen(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editProduct) {
        await api.put(`/products/${editProduct.product_id}`, payload);
        toast.success('Product updated');
      } else if (!navigator.onLine) {
        // No connection at all — skip the doomed request and queue right away.
        await addOfflineProduct(payload);
        toast.success('Saved offline — this item will be created once you reconnect');
        refreshPendingSync();
      } else {
        // Same id sent either way, so a lost response can't create the item twice.
        const offline_id = generateOfflineId();
        try {
          await api.post('/products', { ...payload, offline_id });
          toast.success('Product added');
        } catch (err) {
          if (err.response) throw err; // real server-side error (validation, etc.)
          await addOfflineProduct({ ...payload, offline_id }); // network drop mid-request
          toast.success('Saved offline — this item will be created once you reconnect');
          refreshPendingSync();
        }
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    try {
      await api.delete(`/products/${productId}`);
      toast.success('Product deleted');
      setDeleteConfirm(null);
      fetchProducts();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const addBatch = () => {
    setFormData(prev => ({
      ...prev,
      batches: [...prev.batches, { batch_number: '', expiry_date: '', quantity: 0 }]
    }));
  };

  const updateBatch = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      batches: prev.batches.map((b, i) => i === index ? { ...b, [field]: value } : b)
    }));
  };

  const removeBatch = (index) => {
    setFormData(prev => ({ ...prev, batches: prev.batches.filter((_, i) => i !== index) }));
  };

  const getStockBadge = (product) => {
    if (product.stock_quantity <= 0) return <Badge variant="destructive" className="text-xs">Out of Stock</Badge>;
    if (product.stock_quantity <= product.min_stock_level) return <Badge className="text-xs bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/20">Low Stock</Badge>;
    return <Badge variant="outline" className="text-xs text-emerald-600 dark:text-emerald-400 border-emerald-500/30">In Stock</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Inventory
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{total} products</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingSyncCount > 0 && (
            <button
              type="button"
              onClick={() => setShowSyncReview(true)}
              data-testid="inventory-pending-sync-badge"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
              title="Items created offline on this device, waiting to sync"
            >
              <RefreshCw className="h-3.5 w-3.5" /> {pendingSyncCount} item{pendingSyncCount === 1 ? '' : 's'} waiting to sync
            </button>
          )}
          <Button onClick={openAdd} className="gap-1.5 h-10" data-testid="add-product-btn">
            <Plus className="h-4 w-4" /> Add Product
          </Button>
        </div>
      </div>
      <OfflineFailuresDialog open={showSyncReview} onOpenChange={setShowSyncReview} onChanged={refreshPendingSync} />

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, generic name, manufacturer..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
              data-testid="inventory-search"
            />
          </div>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-44" data-testid="category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stockFilter} onValueChange={(v) => { setStockFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-36" data-testid="stock-filter">
              <SelectValue placeholder="Stock Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stock</SelectItem>
              <SelectItem value="low">Low Stock</SelectItem>
              <SelectItem value="out">Out of Stock</SelectItem>
            </SelectContent>
          </Select>
          <Select value={manufacturerFilter} onValueChange={(v) => { setManufacturerFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-44" data-testid="manufacturer-filter">
              <SelectValue placeholder="Manufacturer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Manufacturers</SelectItem>
              {manufacturers.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card data-testid="inventory-table-card">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Product</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="hidden lg:table-cell">Status</TableHead>
                <TableHead className="text-right w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                skeletonKeys(5, 'inventory-skeleton').map((key) => (
                  <TableRow key={key}>
                    <TableCell colSpan={6}><div className="h-10 bg-muted rounded animate-pulse" /></TableCell>
                  </TableRow>
                ))
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No products found</p>
                  </TableCell>
                </TableRow>
              ) : products.map(product => (
                <TableRow
                  key={product.product_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/inventory/${product.product_id}`)}
                  data-testid={`inv-row-${product.product_id}`}
                >
                  <TableCell>
                    <div>
                      <span className="font-medium text-sm flex items-center gap-1.5">
                        {product.name}
                        {product.requires_prescription && <Badge variant="outline" className="text-[9px] px-1">Rx</Badge>}
                        {product.is_otc && !product.requires_prescription && !product.is_controlled && <Badge variant="outline" className="text-[9px] px-1 whitespace-nowrap" data-testid={`inventory-otc-${product.product_id}`}>{product.otc_test_selection ? 'OTC · Test' : 'OTC'}</Badge>}
                        {product.is_controlled && <Badge variant="destructive" className="text-[9px] px-1">Ctrl</Badge>}
                        {(product.recall_locked || product.batches?.some((b) => b.recalled)) && (
                          <Badge variant="destructive" className="text-[9px] px-1" data-testid={`inventory-recalled-${product.product_id}`}>
                            {product.recall_locked ? 'RECALLED' : 'Batch recalled'}
                          </Badge>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground block">{product.generic_name}</span>
                      {expandedRow === product.product_id && product.batches?.length > 0 && (
                        <div className="mt-2 space-y-1 pl-2 border-l-2 border-primary/20">
                          {keyed(product.batches, b => b.batch_number).map(({ item: b, key }) => (
                            <div key={key} className="text-xs text-muted-foreground flex gap-3">
                              <span>Batch: {b.batch_number}</span>
                              {b.recalled && <span className="text-red-600 font-semibold" data-testid={`inventory-batch-recalled-${b.batch_number}`}>RECALLED — do not sell</span>}
                              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {b.expiry_date?.slice(0, 10)}</span>
                              <span>Qty: {b.quantity}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{product.category}</TableCell>
                  <TableCell className="text-right font-medium text-sm">{fmtCurrency(product.unit_price)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold">{product.stock_quantity}</TableCell>
                  <TableCell className="hidden lg:table-cell">{getStockBadge(product)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); openEdit(product); }}
                        data-testid={`edit-product-${product.product_id}`}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(product.product_id); }}
                        data-testid={`delete-product-${product.product_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t">
            <p className="text-xs text-muted-foreground">Page {page} of {pages}</p>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit Product Dialog — shared with the procurement flows */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          <ProductForm
            productId={editProduct?.product_id}
            value={formData}
            onChange={setFormData}
            onSubmit={handleSave}
            onCancel={() => setDialogOpen(false)}
            saving={saving}
            submitLabel={editProduct ? 'Update Product' : 'Add Product'}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> Delete Product
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Are you sure? This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
